// /opt/isp_erp/frontend/src/pages/admin/clientes/components/CorteCliente.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Box, Paper, Typography, Switch, FormControlLabel, TextField, Button, Alert, Grid, CircularProgress, Chip } from "@mui/material";
import axios from "../../../../api/axios";
import { mensajeError } from "../../../../utils/mensajesError";

const texto = (v) => (v === null || v === undefined ? "" : String(v));

const aFormulario = (d) => ({
  corte_automatico: !!d.corte_automatico,
  dia_corte: texto(d.dia_corte),
  hora_corte: texto(d.hora_corte),
  minuto_corte: texto(d.minuto_corte),
  tolerancia_dias: texto(d.tolerancia_dias),
});

export default function CorteCliente({ customerId }) {
  const [datos, setDatos] = useState(null);
  const [form, setForm] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const r = await axios.get(`/customers/${customerId}/corte`);
      setDatos(r.data);
      setForm(aFormulario(r.data));
    } catch (e) {
      setError(mensajeError(e, "No se pudo cargar la configuración de corte"));
    } finally {
      setCargando(false);
    }
  }, [customerId]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (campo, valor) => {
    setAviso("");
    setForm((f) => ({ ...f, [campo]: valor }));
  };

  const base = datos ? aFormulario(datos) : null;
  const cambios = {};
  let hayVacios = false;
  if (base && form) {
    Object.keys(base).forEach((k) => {
      if (base[k] !== form[k]) {
        if (k === "corte_automatico") cambios[k] = form[k];
        else if (form[k] === "") hayVacios = true;
        else cambios[k] = parseInt(form[k], 10);
      }
    });
  }
  const hayCambios = Object.keys(cambios).length > 0 && !hayVacios;

  const guardar = async () => {
    setGuardando(true);
    setError("");
    setAviso("");
    try {
      const r = await axios.put(`/customers/${customerId}/corte`, cambios);
      setDatos(r.data);
      setForm(aFormulario(r.data));
      setAviso(["Cambios guardados.", ...(r.data.advertencias || [])].join(" "));
    } catch (e) {
      setError(mensajeError(e, "No se pudo guardar el corte"));
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (<Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>);
  }
  if (!datos || !form) {
    return <Alert severity="error">{error || "No se pudo cargar el corte"}</Alert>;
  }
  const editable = !!datos.puede_editar;

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Typography variant="h6">Corte del servicio</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`Estado: ${datos.estado}`} />
          <Chip
            size="small"
            color={datos.facturacion_automatica ? "primary" : "default"}
            label={datos.facturacion_automatica ? "Facturación automática: sí" : "Facturación automática: no"}
          />
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Aquí se guarda cuándo corresponde cortar a este cliente. El sistema todavía no suspende el servicio por sí solo:
        la suspensión se hace desde la ONU o por lote.
      </Alert>
      {!editable && (
        <Alert severity="warning" sx={{ mb: 2 }}>Solo admin, supervisor o finanzas pueden cambiar el corte.</Alert>
      )}

      <FormControlLabel
        control={<Switch checked={form.corte_automatico} onChange={(e) => cambiar("corte_automatico", e.target.checked)} disabled={!editable} />}
        label="Corte automático"
      />

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={6} md={3}>
          <TextField label="Día del mes" type="number" size="small" fullWidth value={form.dia_corte} disabled={!editable}
            onChange={(e) => cambiar("dia_corte", e.target.value)} inputProps={{ min: 1, max: 31 }} helperText="1 a 31" />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField label="Hora" type="number" size="small" fullWidth value={form.hora_corte} disabled={!editable}
            onChange={(e) => cambiar("hora_corte", e.target.value)} inputProps={{ min: 0, max: 23 }} helperText="0 a 23" />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField label="Minuto" type="number" size="small" fullWidth value={form.minuto_corte} disabled={!editable}
            onChange={(e) => cambiar("minuto_corte", e.target.value)} inputProps={{ min: 0, max: 59 }} helperText="0 a 59" />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField label="Días de tolerancia" type="number" size="small" fullWidth value={form.tolerancia_dias} disabled={!editable}
            onChange={(e) => cambiar("tolerancia_dias", e.target.value)} inputProps={{ min: 0, max: 60 }} helperText="0 a 60" />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mt: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Próximo corte</Typography>
          <Typography>{datos.fecha_proximo_corte || "Sin programar"}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Último corte</Typography>
          <Typography>{datos.fecha_ultimo_corte || "—"}</Typography>
        </Box>
      </Box>

      {datos.facturacion_automatica && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Este cliente tiene facturación automática: el día de corte también es su día de facturación.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mt: 2 }}>{aviso}</Alert>}

      <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
        <Button variant="contained" onClick={guardar} disabled={!editable || !hayCambios || guardando}>
          {guardando ? "Guardando..." : "Guardar cambios"}
        </Button>
        <Button onClick={() => { setForm(aFormulario(datos)); setError(""); setAviso(""); }} disabled={!hayCambios && !error}>
          Descartar
        </Button>
      </Box>
    </Paper>
  );
}
